/**
 * Водитель Predict.fun: открыть событие поиском, затем выбрать рынок и исход.
 *
 * Купона в адресе у площадки нет. Человек: два имени → лупа → своя строка
 * «Рынки» → блок победителя → кнопка команды. Повторяем это. Лимитку и
 * «купить» сюда нельзя.
 *
 * Текст карточки («победа …», кэф 1.408) на DOM не проецируется. На экране
 * блок «Победитель матча» и кнопки с именами. Одно имя стоит в матче, карте
 * и форе: «первая ex-Zero» — часто карта 1, не вилка.
 *
 * Два фильтра, оба обязательны. Ноль или два блока / кнопок — отказ.
 * «Похожие рынки» отсекаем: там снова «Победитель матча», но чужой.
 *
 * После выбора — вкладка «Лимитная» и два поля кассы (цена, шеры).
 * «Создать ордер Buy» не жмём: один клик человека, как у контор.
 */

import { Decimal } from 'decimal.js';

import { plain } from '../aim/names';
import { handsOnScroll, showCell } from './showCell';

/** Разделы витрины. Слаг матча сюда не входит. Снято с живого `/markets`. */
const HUB = new Set([
  'crypto',
  'culture',
  'economy',
  'esports',
  'finance',
  'live',
  'mlb',
  'nba',
  'new',
  'nhl',
  'politics',
  'soccer',
  'sports',
  'tech',
  'tennis',
]);

/** Кусок лупы из шапки predict.fun — текст «Поиск рынков» на узком окне снимают. */
const LOUPE_PATH = /22\.45 21L30 28\.59|M25 14C25\.0011/;

export function namesIn(who: readonly string[], text: string): boolean {
  if (who.length === 0) return false;
  const flat = plain(text);
  return who.every((name) => {
    const needle = plain(name);
    return needle !== '' && flat.includes(needle);
  });
}

export function eventHref(href: string): boolean {
  let path = '';
  try {
    path = new URL(href, 'https://predict.fun').pathname;
  } catch {
    return false;
  }
  const parts = path.replace(/\/+$/, '').split('/').filter(Boolean);
  // Событие — только `/market/slug`. `/markets/…` — витрина: лента как раз
  // открывает `…/markets/lol-t1a-ktc-…`, а площадка рисует крипту.
  const at = parts.findIndex((bit) => bit === 'market');
  if (at < 0 || at + 1 >= parts.length) return false;
  return parts[at + 1] !== '';
}

export function pageHref(doc: Document): string {
  const here = doc.defaultView?.location.href ?? '';
  if (/predict\.fun/i.test(here)) return here;
  const base = doc.querySelector('base[href]')?.getAttribute('href');
  return base || here;
}

export function pageText(doc: Document): string {
  const body = doc.body;
  const text = body?.innerText || body?.textContent || '';
  return `${doc.title} ${text}`;
}

/** Заголовок события, не вся витрина: на каталоге та же карточка висит в сетке. */
export function eventHeading(doc: Document): string {
  const titles = [...doc.querySelectorAll('h1')].map((node) => node.textContent ?? '');
  return `${doc.title ?? ''} ${titles.join(' ')}`;
}

export function eventSlug(href: string): string {
  try {
    const parts = new URL(href, 'https://predict.fun').pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    const at = parts.findIndex((bit) => bit === 'market' || bit === 'markets');
    return at >= 0 ? (parts[at + 1] ?? '').toLowerCase() : '';
  } catch {
    return '';
  }
}

export function sameEvent(here: string, dest: string): boolean {
  const a = eventSlug(here);
  const b = eventSlug(dest);
  return a !== '' && a === b;
}

const SLUG_NOISE = new Set([...HUB, 'cs2', 'csgo', 'dota2', 'dota', 'lol', 'valorant', 'val', 'r6']);

function slugTokens(href: string): string[] {
  return eventSlug(href)
    .split('-')
    .filter((bit) => bit.length >= 2 && !/^\d+$/.test(bit) && !SLUG_NOISE.has(bit));
}

/** Слаг `g2` ↔ «G2 Ares». На кнопке «T1» ≠ «T1 Academy» — только токен адреса. */
function slugWordHits(name: string, bit: string): boolean {
  const first = plain(name).split(' ')[0] ?? '';
  const token = plain(bit);
  return first.length >= 2 && token === first;
}

function tokenHits(name: string, bit: string): boolean {
  return nameHits(name, bit) || slugWordHits(name, bit);
}

/** Кнопка или слаг совпал с именем. «GL1 vs FUR» — тоже наши. */
export function whoHits(who: readonly string[], text: string): boolean {
  if (namesIn(who, text)) return true;
  const names = who.filter((name) => name.trim() !== '');
  const tokens = compact(text)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((bit) => bit.length >= 2);
  if (names.length === 0 || tokens.length === 0) return false;
  return names.every((name) => tokens.some((bit) => tokenHits(name, bit)));
}

export function slugHits(href: string, who: readonly string[]): boolean {
  const tokens = slugTokens(href);
  const names = who.filter((name) => name.trim() !== '');
  if (tokens.length < 2 || names.length < 2) return false;
  return names.every((name) => tokens.some((bit) => tokenHits(name, bit)));
}

export function alreadyHere(href: string, heading: string, who: readonly string[]): boolean {
  if (!eventHref(href)) return false;
  return namesIn(who, heading) || whoHits(who, heading) || slugHits(href, who);
}

/** Свой `/market/slug` из обещания. Витрина `/markets/` — нет: там карточка, не касса. */
export function promisedHere(here: string, promised: string): boolean {
  return eventHref(here) && eventHref(promised) && sameEvent(here, promised);
}

/** Чужой /market/ или витрина → адрес события из обещания. Свой слаг не трогаем. */
export function goEventUrl(here: string, promised: string): string | null {
  const fromAsk = eventHref(promised) ? promised : catalogEventUrl(promised);
  if (fromAsk !== null && eventHref(fromAsk) && !sameEvent(here, fromAsk)) return fromAsk;
  return catalogEventUrl(here);
}

export function searchQuery(who: readonly string[]): string {
  return who.filter((name) => name.trim() !== '').join(' ');
}

/** `/markets/slug` витрины → `/market/slug` события. Хаб не трогаем. */
export function liveEventUrl(href: string): string {
  try {
    const url = new URL(href, 'https://predict.fun');
    const parts = url.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    const at = parts.findIndex((bit) => bit === 'markets');
    if (at < 0 || at + 1 >= parts.length) return href;
    const slug = parts[at + 1]!;
    if (HUB.has(slug.toLowerCase())) return href;
    parts[at] = 'market';
    url.pathname = `/${parts.join('/')}`;
    return url.toString();
  } catch {
    return href;
  }
}

export function catalogEventUrl(href: string): string | null {
  const dest = liveEventUrl(href);
  return dest === href ? null : dest;
}

/** Поле оверлея, не призрак шапки: tabindex=-1 и pointer-events-none. */
export function searchField(root: ParentNode): HTMLInputElement | null {
  for (const node of root.querySelectorAll('input')) {
    if (!(node instanceof HTMLInputElement)) continue;
    if (node.type === 'hidden' || node.disabled) continue;
    if (node.tabIndex === -1) continue;
    if (node.classList.contains('pointer-events-none')) continue;
    const cls = typeof node.className === 'string' ? node.className : '';
    if (cls.includes('token-display-body')) return node;
    const ph = (node.getAttribute('placeholder') ?? '').trim().toLowerCase();
    if (ph === 'поиск' || ph === 'search' || ph.includes('поиск рынков') || ph.includes('search markets')) {
      return node;
    }
    if (node.type === 'search') return node;
  }
  return null;
}

function wrapOf(node: Element): HTMLElement {
  const wrap = node.closest('button, [role="button"], a, .cursor-pointer, .rounded-button');
  return wrap instanceof HTMLElement ? wrap : (node as HTMLElement);
}

export function searchOpener(root: ParentNode): HTMLElement | null {
  const want = /поиск рынков|search markets/i;
  for (const node of root.querySelectorAll('button, [role="button"], p, span, div')) {
    if (!(node instanceof HTMLElement)) continue;
    const label = `${node.getAttribute('aria-label') ?? ''} ${node.textContent ?? ''}`;
    const text = label.replace(/\s+/g, ' ').trim();
    if (!want.test(text) || text.length > 64) continue;
    return wrapOf(node);
  }
  for (const path of root.querySelectorAll('svg path')) {
    if (!LOUPE_PATH.test(path.getAttribute('d') ?? '')) continue;
    const svg = path.closest('svg');
    if (svg) return wrapOf(svg);
  }
  for (const link of root.querySelectorAll('a')) {
    const text = (link.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (!/^(пригласить|invite)$/i.test(text)) continue;
    const next = link.nextElementSibling;
    if (next instanceof HTMLElement && next.querySelector('svg')) return wrapOf(next);
  }
  return null;
}

/** Only reveal the target; the runner performs one click through hooks.press. */
export function tap(el: HTMLElement): void {
  showCell(el);
}

export function marketsTab(root: ParentNode): HTMLElement | null {
  for (const node of root.querySelectorAll('[role="tab"]')) {
    if (!(node instanceof HTMLElement)) continue;
    const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (/^рынки$|^markets$/i.test(text)) return node;
  }
  return null;
}

export function searchHits(root: ParentNode): HTMLAnchorElement[] {
  const out: HTMLAnchorElement[] = [];
  const seen = new Set<HTMLAnchorElement>();
  for (const node of root.querySelectorAll('a.global-search-hit-row, a[href*="/market/"]')) {
    if (!(node instanceof HTMLAnchorElement)) continue;
    const href = node.getAttribute('href') ?? '';
    if (!/\/market\//.test(href) || seen.has(node)) continue;
    seen.add(node);
    out.push(node);
  }
  return out;
}

/** Первая строка, в которой есть все имена. Не «что угодно первое». */
export function firstOwnHit(root: ParentNode, who: readonly string[]): HTMLAnchorElement | null {
  for (const hit of searchHits(root)) {
    if (whoHits(who, hit.textContent ?? '')) return hit;
  }
  return null;
}

function nativeSet(field: HTMLInputElement, value: string): void {
  const proto = Object.getPrototypeOf(field) as object;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter !== undefined) setter.call(field, value);
  else field.value = value;
}

export function typeQuery(field: HTMLInputElement, query: string): void {
  field.focus();
  nativeSet(field, query);
  field.dispatchEvent(new InputEvent('input', { bubbles: true, data: query, inputType: 'insertText' }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

export function openByKey(doc: Document): void {
  const view = doc.defaultView;
  const Ctor = view?.KeyboardEvent ?? KeyboardEvent;
  const fire = (extra: KeyboardEventInit): void => {
    const event = new Ctor('keydown', {
      key: '/',
      code: 'Slash',
      bubbles: true,
      cancelable: true,
      ...extra,
    });
    doc.dispatchEvent(event);
    view?.dispatchEvent(event);
  };
  // Человек: Shift+/. Подсказка площадки: ⌘ + /.
  fire({ shiftKey: true });
  fire({ metaKey: true });
  fire({ ctrlKey: true });
}

/** Городские коды MLB/NBA — тот же словарь, что у адаптера. Не «похоже на 80%». */
const CITY_CODES: Record<string, readonly string[]> = {
  stl: ['st louis', 'saint louis'],
  oak: ['oakland', 'athletics'],
  ath: ['athletics', 'oakland'],
  cws: ['white sox'],
  chw: ['white sox'],
  chc: ['cubs'],
  laa: ['angels'],
  lad: ['dodgers'],
  sdp: ['padres'],
  sfg: ['giants'],
  kcr: ['royals'],
  tbr: ['rays'],
  wsh: ['nationals'],
  was: ['nationals', 'wizards', 'capitals'],
  nym: ['mets'],
  nyy: ['yankees'],
  ari: ['diamondbacks'],
  az: ['diamondbacks'],
  gsw: ['warriors', 'golden state'],
  nyk: ['knicks'],
  nop: ['pelicans'],
  okc: ['thunder'],
  sas: ['spurs'],
  uta: ['jazz'],
};

const LINE = /тотал|фора|handicap|total|over|under|спред|spread|больше|меньше|тб|тм/;
const RELATED = /похожие рынки|related markets|^related$|^похожие$/i;
const YES_NO = /^(да|нет|yes|no)$/;
const TICKET = /создать ордер|купить yes|лимитная цена|limit price|create order/;
export const PRICE_LABEL = /лимитная цена|limit price/i;
export const SHARES_LABEL = /шейр|shares/i;
const LIMIT_TAB = /^(лимитная|limit)$/i;
const MARKET_TAB = /^(рыночная|market)$/i;
const LIST_HEAD = /^\d+\s+(исход|outcome)/;

function compact(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function nameCore(text: string): string {
  return compact(text.replace(/\d+\s*¢/g, ' ').replace(/\$[\d.,]+/g, ' '));
}

function hasLine(flat: string): boolean {
  return LINE.test(flat);
}

/**
 * Номер карты/игры. LoL на площадке: «Победитель 3-й игры», не «карта 3».
 * «Тотал 2.5 игр» не карта: без порядкового «й/я» цифру не берём.
 */
function mapNum(flat: string): number | null {
  const after = flat.match(/(?:карта|карты|карте|карт|map|game|игра|игры|игре|гейм)\s+(\d+)/);
  if (after !== null && after[1] !== undefined) return Number(after[1]);
  // plain() снимает кратку с «й» → «и»: «3-й игры» становится «3 и игры».
  const before = flat.match(/(\d+)\s+(?:й|я|е|ой|ая|ья|и)\s+(?:карт|игр|map|game|гейм)/);
  if (before !== null && before[1] !== undefined) return Number(before[1]);
  return null;
}

export function periodKind(period?: string): 'match' | 'map' | 'unknown' {
  const p = (period ?? '').trim().toUpperCase();
  if (p === '' || p === 'M') return 'match';
  if (/^P\d+$/.test(p)) return 'map';
  return 'unknown';
}

function mapPeriod(period?: string): number | null {
  const p = (period ?? '').trim().toUpperCase();
  const found = /^P(\d+)$/.exec(p);
  return found === null ? null : Number(found[1]);
}

/** Заголовок блока совпал с period. «Победитель» без «матча» — не матч. */
export function marketTitle(period: string | undefined, heading: string): boolean {
  const kind = periodKind(period);
  if (kind === 'unknown') return false;
  const flat = plain(heading);
  if (flat === '' || hasLine(flat)) return false;
  const num = mapNum(flat);
  if (kind === 'match') {
    if (num !== null) return false;
    return /победитель матча/.test(flat) || /match winner/.test(flat);
  }
  const want = mapPeriod(period);
  if (want === null || num !== want) return false;
  return /победитель/.test(flat) || /winner/.test(flat) || /(?:карта|карты|карт|map|game)\s+\d+/.test(flat);
}

function anyMarketHeading(flat: string): boolean {
  if (/победитель матча|match winner/.test(flat)) return true;
  if (mapNum(flat) !== null) return true;
  if (flat === 'победитель' || flat === 'winner') return true;
  return hasLine(flat) && /тотал|фора|handicap|total|тб|тм/.test(flat);
}

/** Список «N исходов», не касса справа и не похожие внизу. */
function listRoot(root: ParentNode): ParentNode {
  let best: HTMLElement | null = null;
  let bestLen = Number.POSITIVE_INFINITY;
  for (const node of root.querySelectorAll('div, section, main, aside, article')) {
    if (!(node instanceof HTMLElement)) continue;
    const start = plain(node.textContent ?? '').slice(0, 48);
    if (!LIST_HEAD.test(start)) continue;
    const body = plain(node.textContent ?? '');
    if (!/победитель|тотал|фора|winner|total|handicap|тб|тм/.test(body)) continue;
    const len = (node.textContent ?? '').length;
    if (len < bestLen) {
      best = node;
      bestLen = len;
    }
  }
  return best ?? root;
}

function insideTicket(el: Element): boolean {
  for (let node: Element | null = el; node !== null && node !== document.body; node = node.parentElement) {
    const text = node.textContent ?? '';
    if (text.length > 3500) continue;
    const flat = plain(text);
    if (LIST_HEAD.test(flat.slice(0, 40)) && /победитель/.test(flat)) return false;
    if (TICKET.test(flat)) return true;
  }
  return false;
}

function relatedMark(root: ParentNode): HTMLElement | null {
  for (const node of root.querySelectorAll('h1, h2, h3, h4, h5, p, span, div, section')) {
    if (!(node instanceof HTMLElement)) continue;
    const text = compact(node.textContent ?? '');
    if (text.length === 0 || text.length > 40) continue;
    if (RELATED.test(text)) return node;
  }
  return null;
}

function beforeRelated(el: Element, related: HTMLElement | null): boolean {
  if (related === null) return true;
  if (el === related || related.contains(el)) return false;
  return (el.compareDocumentPosition(related) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
}

/** Заголовки рынков в теле матча. Касса и «Похожие» не считаются. */
export function marketHeadings(root: ParentNode): HTMLElement[] {
  const scope = listRoot(root);
  const related = relatedMark(scope);
  const raw: HTMLElement[] = [];
  for (const node of scope.querySelectorAll('h1, h2, h3, h4, h5, p, span, div, section, article, li')) {
    if (!(node instanceof HTMLElement)) continue;
    if (insideTicket(node)) continue;
    if (!beforeRelated(node, related)) continue;
    const text = compact(node.textContent ?? '');
    if (text.length === 0 || text.length > 80) continue;
    if (!anyMarketHeading(plain(text))) continue;
    raw.push(node);
  }
  return raw.filter((el) => !raw.some((other) => other !== el && el.contains(other)));
}

export function marketBlock(heading: HTMLElement, headings: readonly HTMLElement[]): HTMLElement {
  let node: HTMLElement = heading;
  for (;;) {
    const parent = node.parentElement;
    if (parent === null || parent === document.body || parent === document.documentElement) break;
    if (!(parent instanceof HTMLElement)) break;
    if (headings.some((h) => h !== heading && parent.contains(h))) break;
    node = parent;
  }
  return node;
}

export function ownMarket(root: ParentNode, period?: string): HTMLElement | null {
  if (periodKind(period) === 'unknown') return null;
  const headings = marketHeadings(root);
  const own = headings.filter((h) => marketTitle(period, compact(h.textContent ?? '')));
  if (own.length !== 1) return null;
  return marketBlock(own[0]!, headings);
}

/**
 * Сокращение кнопки похоже на имя участника. Как `looks_like` адаптера:
 * точное, слово, приставка, инициалы, городской код. Не «похоже на 80%».
 */
export function looksLike(short: string, full: string): boolean {
  const s = plain(short);
  const f = plain(full);
  if (s === '' || f === '') return false;
  const bits = f.split(' ').filter(Boolean);
  if (s === f || bits.includes(s) || f.startsWith(s)) return true;
  const initials = bits.map((word) => word[0] ?? '').join('');
  if (s.length >= 2 && initials.startsWith(s)) return true;
  const aliases = CITY_CODES[s];
  return aliases !== undefined && aliases.some((alias) => f.includes(alias));
}

/** GamerLegion → Gamer, Legion. GL1 → GL, 1. */
export function camelBits(name: string): string[] {
  const cleaned = name.replace(/[_-]+/g, ' ').trim();
  const bits: string[] = [];
  for (const word of cleaned.split(/\s+/).filter(Boolean)) {
    const parts = word.match(/[A-ZА-ЯЁ]{2,}(?![a-zа-яё])|[A-ZА-ЯЁ][a-zа-яё]+|[a-zа-яё]+|\d+/g);
    if (parts === null) bits.push(word);
    else bits.push(...parts);
  }
  return bits;
}

function camelCode(name: string): string {
  const bits = camelBits(name);
  if (bits.length < 2) return '';
  return bits.map((bit) => plain(bit)[0] ?? '').join('');
}

function lettersOf(text: string): string {
  return plain(text).replace(/[^a-zа-яё]/g, '');
}

function consonantsOf(text: string): string {
  return lettersOf(text).replace(/[aeiouyаеёиоуыэюя]/g, '');
}

function subseq(needle: string, hay: string): boolean {
  if (needle === '' || hay === '') return false;
  let i = 0;
  for (const ch of hay) {
    if (ch === needle[i]) {
      i += 1;
      if (i === needle.length) return true;
    }
  }
  return false;
}

const SKIP = new Set(['ex', 'the', 'fc', 'fk', 'cf']);

function nameWords(full: string): string[] {
  return plain(full).split(' ').filter(Boolean);
}

function nameBody(full: string): string {
  return nameWords(full)
    .filter((word) => !SKIP.has(word))
    .join('');
}

/** PVN / PRV ← PARIVISION. Три-четыре буквы, не слово «Zero» в ex-Zero. */
function codeHits(short: string, full: string): boolean {
  const s = lettersOf(short);
  const f = lettersOf(full);
  if (s.length < 3 || s.length > 4 || f.length < 6) return false;
  if (s[0] !== f[0]) return false;
  if (nameWords(full).includes(s)) return false;
  const sc = consonantsOf(short);
  const fc = consonantsOf(full);
  return sc.length >= 3 && subseq(sc, fc);
}

/** ESB ← eSuba: буквы короткого по порядку в полном. */
function letterHits(short: string, full: string): boolean {
  const s = lettersOf(short);
  const f = lettersOf(full);
  if (s.length < 3 || s.length > 4 || f.length < 4) return false;
  if (s[0] !== f[0]) return false;
  if (nameWords(full).includes(s)) return false;
  return subseq(s, f);
}

/** ZEROTE ← ex-Zero Tenacity: склейка тела без ex. */
function compactHits(short: string, full: string): boolean {
  const s = lettersOf(short);
  const body = lettersOf(nameBody(full));
  if (s.length < 4 || s.length > 8 || body.length < 6) return false;
  if (nameWords(full).includes(s)) return false;
  return body.includes(s);
}

/** FOR10 ← Fortress: цифры площадки, приставка из букв. */
function stemHits(short: string, full: string): boolean {
  const sl = lettersOf(short);
  const fl = lettersOf(full);
  if (sl.length < 3) return false;
  const first = nameWords(full)[0] ?? '';
  if (sl === lettersOf(first) && fl.length > sl.length && nameWords(full).length > 1) return false;
  return fl.startsWith(sl);
}

/** Кнопка — наше имя целиком или его сокращение. «Zero» ≠ ex-Zero Tenacity. */
export function nameHits(pick: string, label: string): boolean {
  const p = plain(pick);
  const l = plain(label);
  if (p === '' || l === '') return false;
  if (l.includes(p)) return true;
  const short = nameCore(label);
  const s = plain(short);
  const f = p;
  if (s === '' || s === f) return s === f;
  const first = f.split(' ')[0] ?? '';
  // ALL→Alliance да. T1→T1 Academy нет: две буквы при хвосте — чужая команда.
  // DRAMA→Drama eSports да: на кнопке первое слово целиком, не «T1».
  if (f.startsWith(s) && s === first && f.length > first.length) return s.length >= 4;
  if (f.startsWith(s)) return true;
  const bits = f.split(' ').filter(Boolean);
  const initials = bits.map((word) => word[0] ?? '').join('');
  if (s.length >= 2 && initials.startsWith(s)) return true;
  const aliases = CITY_CODES[s];
  if (aliases !== undefined && aliases.some((alias) => f.includes(alias))) return true;
  if (codeHits(s, f) || letterHits(s, f) || compactHits(s, f) || stemHits(s, f)) return true;
  // GL1 ← GamerLegion: код верблюда + необязательная цифра площадки.
  const code = camelCode(pick);
  return code.length >= 2 && s.startsWith(code) && /^\d*$/.test(s.slice(code.length));
}

function isOutcomeLabel(text: string): boolean {
  const t = compact(text);
  if (t === '' || t.length > 64) return false;
  const core = nameCore(t);
  const f = plain(core);
  if (f === '') return false;
  if (/^(общий объём|total volume|volume|график|держатели|активность|комментарии)$/.test(f)) {
    return false;
  }
  if (/купить|продать|buy yes|buy no/.test(f)) return false;
  return /[\p{L}]/u.test(core);
}

export function outcomeButtons(scope: ParentNode): HTMLElement[] {
  const raw: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  const add = (node: Element): void => {
    if (!(node instanceof HTMLElement) || seen.has(node)) return;
    if (!isOutcomeLabel(node.textContent ?? '')) return;
    seen.add(node);
    raw.push(node);
  };
  if (scope instanceof HTMLElement && scope.matches('button, [role="button"], [class*="cursor-pointer"]')) {
    add(scope);
  }
  for (const node of scope.querySelectorAll('button, [role="button"]')) add(node);
  for (const node of scope.querySelectorAll('[class*="cursor-pointer"]')) {
    if (node.closest('button, [role="button"]')) continue;
    add(node);
  }
  return raw.filter((el) => !raw.some((other) => other !== el && el.contains(other)));
}

function siblingOutcomes(heading: HTMLElement, headings: readonly HTMLElement[]): HTMLElement[] {
  const found: HTMLElement[] = [];
  let sib = heading.nextElementSibling;
  while (sib !== null) {
    const node = sib;
    if (headings.some((h) => h === node || node.contains(h))) break;
    if (node instanceof HTMLElement) found.push(...outcomeButtons(node));
    sib = node.nextElementSibling;
  }
  return found;
}

function pickFromButtons(buttons: HTMLElement[], pick: string, rival: string): HTMLElement | null {
  if (buttons.length !== 2) return null;
  const labels = buttons.map((b) => plain(nameCore(b.textContent ?? '')));
  if (labels.every((one) => YES_NO.test(one))) return null;
  const hits = buttons.filter((b) => nameHits(pick, b.textContent ?? ''));
  if (hits.length !== 1) return null;
  const other = buttons.find((b) => b !== hits[0]);
  if (rival.trim() !== '' && other !== undefined && !nameHits(rival, other.textContent ?? '')) {
    return null;
  }
  return hits[0] ?? null;
}

function lineHas(flat: string, line?: string): boolean {
  const n = plain(line ?? '');
  return n === '' || flat.includes(n);
}

function totalTitle(period: string | undefined, heading: string, line?: string): boolean {
  const kind = periodKind(period);
  if (kind === 'unknown') return false;
  const flat = plain(heading);
  if (flat === '') return false;
  if (!/тотал|тб|тм|total/.test(flat)) return false;
  if (!lineHas(flat, line)) return false;
  const num = mapNum(flat);
  if (kind === 'match') {
    if (num !== null) return false;
    if (/раунд/.test(flat)) return false;
    return /гейм|игр|карт|game|тб|тм/.test(flat);
  }
  const want = mapPeriod(period);
  return want !== null && num === want;
}

/** Фора серии по картам или фора карты N по раундам. Не победитель. */
export function handicapTitle(period: string | undefined, heading: string, line?: string): boolean {
  const kind = periodKind(period);
  if (kind === 'unknown') return false;
  const flat = plain(heading);
  if (flat === '' || !/фора|handicap/.test(flat)) return false;
  if (!lineHas(flat, line)) return false;
  const num = mapNum(flat);
  if (kind === 'match') {
    if (num !== null) return false;
    if (/раунд/.test(flat)) return false;
    return /карт|map|game|гейм/.test(flat);
  }
  const want = mapPeriod(period);
  return want !== null && num === want;
}

function totalHits(side: string, label: string): boolean {
  const f = plain(nameCore(label));
  if (f === '') return false;
  if (side === 'O' || side === 'over') return f.startsWith('больше') || f.startsWith('over');
  if (side === 'U' || side === 'under') return f.startsWith('меньше') || f.startsWith('under');
  return false;
}

function buttonsOf(heading: HTMLElement, headings: readonly HTMLElement[]): HTMLElement[] {
  const block = marketBlock(heading, headings);
  const inside = outcomeButtons(block);
  return inside.length > 0 ? inside : siblingOutcomes(heading, headings);
}

function ownTotal(
  root: ParentNode,
  period: string | undefined,
  side: string,
  line?: string,
): HTMLElement | null {
  const headings = marketHeadings(root);
  const own = headings.filter((h) => totalTitle(period, compact(h.textContent ?? ''), line));
  if (own.length !== 1) return null;
  const buttons = buttonsOf(own[0]!, headings);
  if (buttons.length !== 2) return null;
  const hits = buttons.filter((b) => totalHits(side, b.textContent ?? ''));
  return hits.length === 1 ? hits[0]! : null;
}

function ownHcp(
  root: ParentNode,
  period: string | undefined,
  pick: string,
  rival: string,
  line?: string,
): HTMLElement | null {
  if (plain(pick) === '' || periodKind(period) === 'unknown') return null;
  const headings = marketHeadings(root);
  const own = headings.filter((h) => handicapTitle(period, compact(h.textContent ?? ''), line));
  if (own.length !== 1) return null;
  return pickFromButtons(buttonsOf(own[0]!, headings), pick, rival);
}

function ownWin(
  root: ParentNode,
  period: string | undefined,
  pick: string,
  rival: string,
): HTMLElement | null {
  if (plain(pick) === '' || periodKind(period) === 'unknown') return null;
  const headings = marketHeadings(root);
  const own = headings.filter((h) => marketTitle(period, compact(h.textContent ?? '')));
  if (own.length !== 1) return null;
  return pickFromButtons(buttonsOf(own[0]!, headings), pick, rival);
}

/** Ровно один свой блок и ровно одна своя кнопка. Иначе null. */
export function ownOutcome(
  root: ParentNode,
  period: string | undefined,
  pick: string,
  rival: string,
  side?: string,
  kind?: string,
  line?: string,
): HTMLElement | null {
  const family = (kind ?? '').toUpperCase();
  if (family === 'ITTL') return null;
  if (family === 'TTL' || side === 'O' || side === 'U' || side === 'over' || side === 'under') {
    return ownTotal(root, period, side ?? '', line);
  }
  if (family === 'HCP') return ownHcp(root, period, pick, rival, line);
  if (family !== '' && family !== 'WIN') return null;
  return ownWin(root, period, pick, rival);
}

/** Рынок в теле события. Касса справа — ещё не готово. «N исходов» не обязателен: один рынок его не рисует. */
export function marketsReady(root: ParentNode): boolean {
  return marketHeadings(root).length > 0;
}

export function scanOutcome(
  root: ParentNode,
  period: string | undefined,
  pick: string,
  rival: string,
  side?: string,
  kind?: string,
  line?: string,
): { cell: HTMLElement | null; ready: boolean } {
  const cell = ownOutcome(root, period, pick, rival, side, kind, line);
  if (cell !== null) return { cell, ready: true };
  const headings = marketHeadings(root);
  const family = (kind ?? '').toUpperCase();
  const total = family === 'TTL' || side === 'O' || side === 'U' || side === 'over' || side === 'under';
  // Победитель наверху — ещё не тотал и не карта. Иначе until останавливается на пустой кассе.
  if (total) {
    return { cell: null, ready: headings.some((h) => totalTitle(period, compact(h.textContent ?? ''), line)) };
  }
  if (family === 'HCP') {
    return { cell: null, ready: headings.some((h) => handicapTitle(period, compact(h.textContent ?? ''), line)) };
  }
  if (periodKind(period) === 'map') {
    const has = headings.some((h) => marketTitle(period, compact(h.textContent ?? '')));
    return { cell: null, ready: has };
  }
  return { cell: null, ready: marketsReady(root) };
}

/** Список «N исходов»: крутим, пока не всплывёт свой рынок. Виртуализация иначе молчит. */
export function revealPeriod(root: ParentNode, period?: string, kind?: string): void {
  if (handsOnScroll()) return;
  const family = (kind ?? '').toUpperCase();
  const headings = marketHeadings(root);
  const hit = family === 'TTL'
    ? headings.find((h) => totalTitle(period, compact(h.textContent ?? '')))
    : family === 'HCP'
      ? headings.find((h) => handicapTitle(period, compact(h.textContent ?? '')))
      : periodKind(period) === 'map'
        ? headings.find((h) => marketTitle(period, compact(h.textContent ?? '')))
        : undefined;
  if (hit === undefined && family !== 'TTL' && family !== 'HCP' && periodKind(period) !== 'map') return;
  if (hit !== undefined) {
    showCell(hit);
    return;
  }
  const list = listRoot(root);
  if (!(list instanceof HTMLElement) || list === root) return;
  const step = Math.max(list.clientHeight, 80);
  list.scrollTop += step;
  const last = list.lastElementChild;
  if (last instanceof HTMLElement) {
    showCell(last, { block: 'end', inline: 'nearest' });
  }
}

/** 1.408 → 71. Целыми: 100 / кэф, без float. */
export function impliedCents(odds: string): number | null {
  if (!/^\d+(\.\d+)?$/.test(odds)) return null;
  const [whole, frac = ''] = odds.split('.');
  const den = Number.parseInt(`${whole}${frac}`, 10);
  if (!Number.isFinite(den) || den <= 0) return null;
  const num = 100 * 10 ** frac.length;
  return Math.trunc((num + Math.floor(den / 2)) / den);
}

export function centsNear(text: string): number | null {
  const found = text.match(/(\d+)\s*¢/);
  if (found === null || found[1] === undefined) return null;
  return Number.parseInt(found[1], 10);
}

/** После выбора, не для поиска. Нет числа на кнопке — не врём. */
export function priceMoved(text: string, odds: string): boolean {
  const got = centsNear(text);
  const want = impliedCents(odds);
  if (got === null || want === null) return false;
  return Math.abs(got - want) > 3;
}

/** Официальный минимум ордера Predict.fun — 1 USDT. Шеры сами не накручиваем. */
export const MIN_ORDER_USD = '1';

/** Целые центы из обещания. Пусто — старое расширение, кассу не трогаем. */
export function ticketCents(ask: { cents?: string; limitPrice?: string }): string | null {
  if (ask.limitPrice !== undefined) {
    if (!/^0\.\d{1,6}$/.test(ask.limitPrice) || /\s/.test(ask.limitPrice)) return null;
    const price = new Decimal(ask.limitPrice);
    return price.gt(0) && price.lt(1) ? price.times(100).toFixed() : null;
  }
  const raw = ask.cents;
  if (raw === undefined || !/^\d+$/.test(raw) || raw === '0') return null;
  return raw;
}

export function ticketShares(ask: { shares?: string }): string | null {
  const raw = ask.shares;
  if (raw === undefined || !/^\d+(\.\d{1,4})?$/.test(raw) || raw === '0') return null;
  return raw;
}

export function ticketUsd(cents: string, shares: string): Decimal {
  return new Decimal(cents).div(100).times(shares);
}

export function ticketReady(cents: string, shares: string): boolean {
  return ticketUsd(cents, shares).gte(MIN_ORDER_USD);
}

function cashText(node: HTMLElement): string {
  return plain(node.textContent ?? '');
}

function isCashPanel(node: HTMLElement): boolean {
  const flat = cashText(node);
  // «Создать ордер Buy» одно — не касса: иначе ticketRoot берёт обёртку кнопки
  // без полей, и живой SteppedInput рядом остаётся «лимитку не нашёл».
  return PRICE_LABEL.test(flat) && SHARES_LABEL.test(flat);
}

/** Касса целиком: цена и шеры вместе. Одна строка «Лимитная цена» — ещё не касса. */
export function ticketRoot(root: ParentNode): HTMLElement | null {
  let best: HTMLElement | null = null;
  let bestLen = Number.POSITIVE_INFINITY;
  for (const node of root.querySelectorAll('aside, form, section, div, [role="complementary"]')) {
    if (!(node instanceof HTMLElement)) continue;
    const text = node.textContent ?? '';
    if (text.length === 0 || text.length > 16_000) continue;
    if (!isCashPanel(node)) continue;
    if (text.length < bestLen) {
      best = node;
      bestLen = text.length;
    }
  }
  return best;
}

function tabText(el: HTMLElement): string {
  return compact(el.textContent ?? '');
}

function findTab(root: ParentNode, want: RegExp): HTMLElement | null {
  const scope = ticketRoot(root) ?? root;
  for (const node of scope.querySelectorAll('button, [role="tab"], a')) {
    if (!(node instanceof HTMLElement)) continue;
    const text = tabText(node);
    if (text.length === 0 || text.length > 24) continue;
    if (want.test(text)) return node;
  }
  return null;
}

export function limitTab(root: ParentNode): HTMLElement | null {
  return findTab(root, LIMIT_TAB);
}

export function tabOn(el: HTMLElement): boolean {
  if (el.getAttribute('aria-selected') === 'true') return true;
  if (el.getAttribute('aria-pressed') === 'true') return true;
  const state = el.getAttribute('data-state');
  if (state === 'active' || state === 'selected') return true;
  return /\b(active|selected|current)\b/i.test(el.className);
}

/** Вкладку жмём только если «Лимитная» ещё не выбрана. Повтор может снять её. */
export function needLimitTab(root: ParentNode): HTMLElement | null {
  // Current native UI uses a Radix order-type dropdown. The chart also says
  // "Market", so only the unique menu trigger is eligible here.
  const triggers = [...root.querySelectorAll<HTMLElement>('[aria-haspopup="menu"]')]
    .filter(el => MARKET_TAB.test(tabText(el)) && !el.matches('[aria-disabled="true"], :disabled'));
  if (triggers.length > 1) return null;
  if (triggers.length === 1) {
    const trigger = triggers[0]!;
    if (trigger.getAttribute('aria-expanded') !== 'true' && trigger.getAttribute('data-state') !== 'open') return trigger;
    const items = [...root.querySelectorAll<HTMLElement>('[role="menuitem"]')]
      .filter(el => LIMIT_TAB.test(tabText(el)) && !el.matches('[aria-disabled="true"], [data-disabled]'));
    return items.length === 1 ? items[0]! : null;
  }
  const tab = limitTab(root);
  if (tab === null || tabOn(tab)) return null;
  const market = findTab(root, MARKET_TAB);
  if (market !== null && !tabOn(market) && !tabOn(tab)) return tab;
  return tab;
}

function labelledBy(el: HTMLElement): string {
  const ids = el.getAttribute('aria-labelledby') ?? '';
  if (ids === '') return '';
  const doc = el.ownerDocument;
  if (doc === null) return '';
  return ids
    .split(/\s+/)
    .map((id) => doc.getElementById(id)?.textContent ?? '')
    .join(' ');
}

function nearestCaption(el: HTMLElement): string {
  let node: HTMLElement | null = el.parentElement;
  for (let i = 0; i < 5 && node !== null; i += 1) {
    const t = compact(node.textContent ?? '');
    node = node.parentElement;
    if (t.length === 0 || t.length >= 120) continue;
    const hasPrice = PRICE_LABEL.test(t);
    const hasShares = SHARES_LABEL.test(t);
    if (hasPrice === hasShares) continue;
    return t;
  }
  return '';
}

function fieldBits(el: HTMLElement): string {
  const box = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ? el : null;
  const wrap = el.closest('label')?.textContent ?? '';
  const prev = el.previousElementSibling?.textContent ?? '';
  return [
    box?.name ?? '',
    box?.placeholder ?? '',
    el.getAttribute('aria-label') ?? '',
    labelledBy(el),
    wrap,
    prev,
    nearestCaption(el),
  ].join(' ');
}

const FIELD_SEL =
  'input, textarea, [contenteditable="true"], [role="spinbutton"], [inputmode="decimal"], [inputmode="numeric"]';

function isTicketInput(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement) return el.type !== 'hidden';
  if (el instanceof HTMLTextAreaElement) return true;
  if (el.isContentEditable) return true;
  const role = el.getAttribute('role');
  if (role === 'spinbutton' || role === 'textbox') return true;
  const mode = el.getAttribute('inputmode');
  return mode === 'decimal' || mode === 'numeric';
}

function fieldAfterCaption(scope: ParentNode, want: RegExp): HTMLElement | null {
  for (const node of scope.querySelectorAll('p, span, div, label, h3, h4, dt, legend, button')) {
    if (!(node instanceof HTMLElement)) continue;
    const text = compact(node.textContent ?? '');
    if (text.length === 0 || text.length > 40) continue;
    if (!want.test(text)) continue;
    const home = node.parentElement ?? node;
    for (const el of home.querySelectorAll(FIELD_SEL)) {
      if (isTicketInput(el) && (node.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) {
        return el;
      }
    }
  }
  return null;
}

/** Поле кассы по подписи, не по классу и не общее «сумма/ставка». */
export function ticketField(root: ParentNode, want: RegExp): HTMLElement | null {
  const scope = ticketRoot(root) ?? root;
  const hits: HTMLElement[] = [];
  const take = (el: HTMLElement | null): void => {
    if (el === null || !isTicketInput(el) || hits.includes(el)) return;
    hits.push(el);
  };
  for (const el of scope.querySelectorAll(FIELD_SEL)) {
    if (isTicketInput(el) && want.test(fieldBits(el))) take(el);
  }
  for (const label of scope.querySelectorAll('label')) {
    if (!want.test(label.textContent ?? '')) continue;
    const id = label.htmlFor;
    if (id !== '') take((label.ownerDocument ?? document).getElementById(id));
    const inner = label.querySelector(FIELD_SEL);
    if (inner instanceof HTMLElement) take(inner);
  }
  if (hits[0] !== undefined) return hits[0];
  return fieldAfterCaption(scope, want);
}
